import React, { useState, useEffect } from 'react'
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Card, CardContent } from '@/components/ui/card'
import { motion, AnimatePresence } from 'framer-motion'
import { useGetHealthQuery, ProjectHealth } from '@/redux/service/project'
import HealthModel from '@/components/health/HealthModel'
import { CustomPagination } from '@/components/CustomPagination'
import { useDebounce } from '@/lib/useDebounce'
import ReusableDateRangePicker, {
  DateRange,
} from '@/components/ReusableDateRangePicker'
import { Input } from '@/components/ui/input'
import {
  Eye,
  Search,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  FilterIcon,
  X,
  Check,
  ChevronDown,
  Loader2,
} from 'lucide-react'
import { formatCurrency, formatHours, cn } from '@/lib/utils'
import dayjs from 'dayjs'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useCheckPermission } from '@/hooks/useCheckPermission'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useGetClientSelectOptionsQuery } from '@/redux/service/client'
import { ProjectWorkspaceTabs } from '@/components/ProjectWorkspaceTabs'
import { ColumnVisibilityPopover } from '@/components/ColumnVisibilityPopover'

const DEFAULT_COLUMNS = [
  { key: 'project', label: 'Project' },
  { key: 'type', label: 'Type' },
  { key: 'task_progress', label: 'Task Progress' },
  { key: 'timeline', label: 'Timeline' },
  { key: 'capacity', label: 'Capacity' },
  { key: 'profit_budget', label: 'Profit / Budget' },
  { key: 'symptoms', label: 'Symptoms' },
]

const ModernProgressBar = ({
  progress,
  className = '',
  colorClass = 'bg-primary',
  label,
  subLabel,
}: {
  progress: number
  className?: string
  colorClass?: string
  label?: React.ReactNode
  subLabel?: React.ReactNode
}) => (
  <div
    className={`flex flex-col gap-1.5 w-full min-w-[240px] max-w-[300px] ${className}`}
  >
    <div className='flex justify-between items-center px-0.5'>
      <div className='text-[10px] font-bold text-muted-foreground uppercase tracking-wider truncate mr-2'>
        {label}
      </div>
      <div className='text-[10px] font-black text-foreground whitespace-nowrap'>
        {subLabel}
      </div>
    </div>
    <div className='h-2 w-full bg-muted/40 rounded-full overflow-hidden border border-border/40 shadow-inner relative'>
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
        transition={{ duration: 1, ease: 'easeOut' }}
        className={`h-full rounded-full shadow-[0_0_8px_-2px_rgba(0,0,0,0.1)] ${colorClass}`}
      />
    </div>
  </div>
)

const Health = () => {
  const {
    hasPageAccess,
    isTeamLeader,
    isLoading: isProfileLoading,
  } = useCheckPermission()
  const hideTLFilters = isProfileLoading || isTeamLeader
  const canSeeFinance =
    hasPageAccess('finance') || hasPageAccess('mini_finance')
  const isFullFinance = hasPageAccess('finance')
  const [page, setPage] = React.useState(1)
  const [limit, setLimit] = React.useState(50)
  const [searchQuery, setSearchQuery] = React.useState('')
  const debouncedSearch = useDebounce(searchQuery, 500)
  const [dateRange, setDateRange] = React.useState<DateRange>({
    from: undefined,
    to: undefined,
  })
  const [projectType, setProjectType] = React.useState<string>('all')
  const [scheduleStatus, setScheduleStatus] = React.useState<string>('all')
  const [missingDate, setMissingDate] = React.useState<boolean>(false)
  const [filterCard, setFilterCard] = useState(false)
  const [clientSearch, setClientSearch] = useState('')
  const [clientSearchInput, setClientSearchInput] = useState('')
  const [isClientPopoverOpen, setIsClientPopoverOpen] = useState(false)
  const debouncedClientSearch = useDebounce(clientSearch, 500)
  const debouncedClientDropdownSearch = useDebounce(clientSearchInput, 500)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const [columns, setColumns] = useState<{ key: string; label: string }[]>(
    () => {
      const savedOrder = localStorage.getItem('health-table-column-order')
      if (savedOrder) {
        try {
          const parsedOrder = JSON.parse(savedOrder)
          return parsedOrder
            .map((key: string) => DEFAULT_COLUMNS.find(c => c.key === key))
            .filter(Boolean) as { key: string; label: string }[]
        } catch {
          // Fallback
        }
      }
      return DEFAULT_COLUMNS
    }
  )

  const [selectedColumns, setSelectedColumns] = useState<string[]>(() => {
    const savedVisibility = localStorage.getItem('health-table-columns')
    if (savedVisibility) {
      try {
        return JSON.parse(savedVisibility)
      } catch {
        // Fallback
      }
    }
    return DEFAULT_COLUMNS.map(col => col.key)
  })

  useEffect(() => {
    localStorage.setItem(
      'health-table-column-order',
      JSON.stringify(columns.map(c => c.key))
    )
  }, [columns])

  useEffect(() => {
    localStorage.setItem(
      'health-table-columns',
      JSON.stringify(selectedColumns)
    )
  }, [selectedColumns])

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      setColumns(items => {
        const oldIndex = items.findIndex(i => i.key === active.id)
        const newIndex = items.findIndex(i => i.key === over.id)
        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }

  const toggleColumn = (key: string) => {
    setSelectedColumns(prev =>
      prev.includes(key)
        ? prev.filter(c => c !== key && selectedColumns.length > 1)
        : [...prev, key]
    )
  }

  const isColumnVisible = (key: string) => {
    if (key === 'profit_budget' && !canSeeFinance) return false
    return selectedColumns.includes(key)
  }

  const params = {
    page,
    limit,
    search: debouncedSearch,
    client: debouncedClientSearch,
    ...(dateRange.from && {
      startDate: dayjs(dateRange.from).format('YYYY-MM-DD'),
    }),
    ...(dateRange.to && {
      endDate: dayjs(dateRange.to).format('YYYY-MM-DD'),
    }),
    ...(projectType !== 'all' && { projectType }),
    ...(scheduleStatus !== 'all' && { scheduleStatus }),
    ...(missingDate && { missingDate }),
  }

  const {
    data: healthResponse,
    isLoading,
    isError,
    refetch,
  } = useGetHealthQuery(params)

  const {
    data: clientsNames,
    isLoading: clientLoading,
    isFetching: clientFetching,
  } = useGetClientSelectOptionsQuery({
    search: debouncedClientDropdownSearch,
    page: 1,
    limit: 50,
  })

  const healthData = healthResponse?.data
  const pagination = healthResponse?.pagination
  const totalPages = pagination
    ? Math.ceil(pagination.total / pagination.limit)
    : 1
  const totalRecords = healthResponse?.pagination?.total || 0
  const [selectedProject, setSelectedProject] =
    React.useState<ProjectHealth | null>(null)
  const [isModalOpen, setIsModalOpen] = React.useState(false)

  const handleOpenModal = (project: ProjectHealth) => {
    setSelectedProject(project)
    setIsModalOpen(true)
  }

  const handleReset = () => {
    setSearchQuery('')
    setClientSearch('')
    setClientSearchInput('')
    setDateRange({ from: undefined, to: undefined })
    setPage(1)
    setProjectType('all')
    setScheduleStatus('all')
    setMissingDate(false)
  }

  if (isLoading && !healthData) {
    return (
      <div className='flex items-center justify-center min-h-[400px]'>
        <div className='flex flex-col items-center gap-4'>
          <div className='w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin' />
          <p className='text-muted-foreground animate-pulse font-medium'>
            Fetching health data...
          </p>
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className='flex items-center justify-center min-h-[400px]'>
        <Card className='w-full max-w-md border-destructive/20 bg-destructive/5'>
          <CardContent className='flex flex-col items-center gap-4 pt-6'>
            <AlertCircle className='w-12 h-12 text-destructive' />
            <div className='text-center'>
              <h3 className='text-lg font-semibold text-destructive'>
                Connection Error
              </h3>
              <p className='text-muted-foreground text-sm'>
                Failed to load health metrics. Please check your connection and
                try again.
              </p>
            </div>
            <button
              onClick={() => refetch()}
              className='px-4 py-2 bg-destructive text-white rounded-lg hover:bg-destructive/90 transition-colors text-sm font-medium'
            >
              Retry Connection
            </button>
          </CardContent>
        </Card>
      </div>
    )
  }

  const visibleColumnsCount =
    columns.filter(col => isColumnVisible(col.key)).length + 1

  return (
    <TooltipProvider>
      <div className='h-full flex flex-col space-y-4 overflow-hidden'>
        <div className='shrink-0 flex flex-col lg:flex-row lg:items-center justify-between border-b border-border pb-3 mb-4 gap-4'>
          <ProjectWorkspaceTabs hidePendingSetup={hideTLFilters} />

          <div className='flex flex-col sm:flex-row items-center gap-3 w-full lg:w-auto'>
            <div className='relative w-full sm:w-48 shrink-0'>
              <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500' />
              <Input
                placeholder='Search projects, lists...'
                value={searchQuery}
                onChange={e => {
                  setSearchQuery(e.target.value)
                  setPage(1)
                }}
                className='pl-10 h-9 border-border text-foreground focus:border-primary transition-all rounded-lg text-xs bg-card'
              />
            </div>

            <div className='relative w-full sm:w-40 shrink-0'>
              <Popover
                open={isClientPopoverOpen}
                onOpenChange={setIsClientPopoverOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant='secondary'
                    role='combobox'
                    aria-expanded={isClientPopoverOpen}
                    className='w-full h-9 justify-between border-border bg-card hover:!bg-transparent hover:text-foreground text-foreground transition-all rounded-lg px-3 text-xs'
                  >
                    <span className='truncate text-xs font-normal'>
                      {clientSearch || 'Search by client...'}
                    </span>
                    <ChevronDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className='w-[var(--radix-popover-trigger-width)] p-0 bg-popover border-border shadow-xl rounded-xl z-[100] '
                  align='start'
                >
                  <div className='flex flex-col h-full max-h-[300px]'>
                    <div className='flex items-center border-b border-border p-2'>
                      <div className='flex items-center relative bg-muted rounded-md py-1 flex-1'>
                        <Search className=' absolute h-4 w-4 shrink-0 text-muted-foreground ms-2' />
                        <Input
                          placeholder='Search clients...'
                          value={clientSearchInput}
                          onChange={e => setClientSearchInput(e.target.value)}
                          className='h-8 w-full border-none  text-sm ps-7'
                          autoFocus
                        />
                      </div>
                      {clientSearchInput && (
                        <Button
                          variant='ghost'
                          size='icon'
                          className='h-6 w-6 hover:bg-muted'
                          onClick={() => setClientSearchInput('')}
                        >
                          <X className='h-3 w-3' />
                        </Button>
                      )}
                    </div>
                    <div className='overflow-y-auto py-1 '>
                      {clientLoading || clientFetching ? (
                        <div className='flex items-center justify-center py-6 px-4'>
                          <Loader2 className='h-4 w-4 animate-spin text-muted-foreground mr-2' />
                          <span className='text-xs text-muted-foreground'>
                            Loading clients...
                          </span>
                        </div>
                      ) : clientsNames?.data?.length === 0 ? (
                        <div className='py-6 text-center text-xs text-muted-foreground'>
                          No clients found
                        </div>
                      ) : (
                        <>
                          <button
                            onClick={() => {
                              setClientSearch('')
                              setClientSearchInput('')
                              setPage(1)
                              setIsClientPopoverOpen(false)
                            }}
                            className='w-full flex items-center px-3 py-2 text-xs hover:bg-accent transition-colors'
                          >
                            <span className='flex-1 text-left'>
                              All Clients
                            </span>
                          </button>
                          {clientsNames?.data?.map(client => (
                            <button
                              key={client.name}
                              onClick={() => {
                                setClientSearch(client.name)
                                setClientSearchInput('')
                                setPage(1)
                                setIsClientPopoverOpen(false)
                              }}
                              className={`w-full flex items-center px-3 py-2 text-xs hover:bg-accent transition-colors ${
                                clientSearch === client.name
                                  ? 'bg-accent/50 font-medium text-primary'
                                  : 'text-foreground'
                              }`}
                            >
                              <span className='flex-1 text-left truncate'>
                                {client.name ?? '—'}
                              </span>
                              {clientSearch === client.name && (
                                <Check className='h-3 w-3 text-primary ml-2' />
                              )}
                            </button>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            <Button
              onClick={() => setFilterCard(prev => !prev)}
              variant='secondary'
              size='sm'
              className='flex gap-2 h-9 text-xs border-border w-full sm:w-auto justify-center'
            >
              <FilterIcon className='w-3 h-3' />
            </Button>

            <ColumnVisibilityPopover
              columns={columns}
              isColumnVisible={isColumnVisible}
              onToggle={toggleColumn}
              onReset={() => {
                setColumns(DEFAULT_COLUMNS)
                setSelectedColumns(DEFAULT_COLUMNS.map(c => c.key))
              }}
              onDragEnd={handleDragEnd}
              sensors={sensors}
              showLabel={true}
              triggerClassName='flex gap-2 h-9 text-xs border-border bg-card hover:!bg-transparent hover:text-foreground text-foreground transition-all rounded-lg px-3'
            />
          </div>
        </div>

        <AnimatePresence>
          {filterCard && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className='shrink-0 overflow-hidden mb-6'
            >
              <div className='border border-border rounded-xl bg-card p-6 shadow-sm space-y-4'>
                <div className='flex items-center justify-between px-1'>
                  <div className='flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground/70'>
                    Advanced Filters
                  </div>
                  <Button
                    variant='ghost'
                    size='sm'
                    onClick={handleReset}
                    className='h-7 px-2 text-[10px] uppercase tracking-wider font-bold text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors flex items-center gap-1.5'
                  >
                    <X className='w-3 h-3' /> Reset All
                  </Button>
                </div>

                <div className='grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2'>
                  <ReusableDateRangePicker
                    value={dateRange}
                    onSelect={range => {
                      setDateRange(range)
                      setPage(1)
                    }}
                    className='h-9 w-full'
                  />

                  <Select
                    value={projectType}
                    onValueChange={val => {
                      setProjectType(val)
                      setPage(1)
                    }}
                  >
                    <SelectTrigger className='h-9 w-full border-border  hover:bg-accent/30 transition-colors text-xs rounded-lg'>
                      <SelectValue placeholder='Project Type' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='all'>All Types</SelectItem>
                      <SelectItem value='Upwork Tracking'>
                        Upwork Tracking
                      </SelectItem>
                      <SelectItem value='Outside Fix'>Outside Fix</SelectItem>
                      <SelectItem value='Internal'>Internal</SelectItem>
                      <SelectItem value='Upwork Fix'>Upwork Fix</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select
                    value={scheduleStatus}
                    onValueChange={val => {
                      setScheduleStatus(val)
                      setPage(1)
                    }}
                  >
                    <SelectTrigger className='h-9 w-full border-border  hover:bg-accent/30 transition-colors text-xs rounded-lg'>
                      <SelectValue placeholder='Status' />
                    </SelectTrigger>
                    <SelectContent className='capitalize'>
                      <SelectItem value='all'>All Status</SelectItem>
                      <SelectItem value='bad debt (payment dropped)'>
                        bad debt
                      </SelectItem>
                      <SelectItem value='client review'>
                        client review
                      </SelectItem>
                      <SelectItem value='completed'>completed</SelectItem>
                      <SelectItem value='in progress'>in progress</SelectItem>
                      <SelectItem value='moved to sales'>
                        moved to sales
                      </SelectItem>
                      <SelectItem value='on demand'>on demand</SelectItem>
                      <SelectItem value='on-hold'>on-hold</SelectItem>
                    </SelectContent>
                  </Select>

                  <div className='flex items-center gap-3 px-4 h-9 bg-muted/50 border border-border/50 rounded-xl hover:border-primary/30 transition-all group shrink-0'>
                    <Switch
                      id='missing-date'
                      checked={missingDate}
                      onCheckedChange={val => {
                        setMissingDate(val)
                        setPage(1)
                      }}
                      className='data-[state=checked]:bg-primary'
                    />
                    <Label
                      htmlFor='missing-date'
                      className='text-[10px] font-bold uppercase tracking-wider text-muted-foreground group-hover:text-primary cursor-pointer transition-colors'
                    >
                      Missing Dates
                    </Label>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className='flex-1 border rounded-sm overflow-auto'>
          <Table className='min-w-[1200px] [&_th]:h-9 [&_th]:px-3 [&_td]:px-3 [&_td]:py-2 border-none'>
            <TableHeader>
              <TableRow className='border-border'>
                {columns.map(column => {
                  if (!isColumnVisible(column.key)) return null

                  const isSticky = column.key === 'project'
                  return (
                    <TableHead
                      key={column.key}
                      className={cn(
                        'font-medium text-foreground truncate',
                        isSticky &&
                          'sticky left-0 z-30 bg-background w-[300px] min-w-[300px] max-w-[300px]'
                      )}
                    >
                      {column.label}
                    </TableHead>
                  )
                })}
                <TableHead className='font-medium text-foreground truncate'>
                  Action
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <AnimatePresence mode='wait'>
                {healthData?.length ? (
                  healthData.map((project: ProjectHealth, index: number) => {
                    const completed = Number(project.completed_tasks)
                    const total = Number(project.total_tasks)
                    const progress = total > 0 ? (completed / total) * 100 : 0

                    return (
                      <motion.tr
                        key={project.project_id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.03 }}
                        className='group border-b border-border hover:bg-muted/30 transition-colors'
                      >
                        {columns.map(column => {
                          if (!isColumnVisible(column.key)) return null

                          switch (column.key) {
                            case 'project':
                              return (
                                <TableCell
                                  key={column.key}
                                  className='sticky left-0 z-20 bg-background w-[300px] min-w-[300px] max-w-[300px] align-middle'
                                >
                                  <div className='flex flex-col gap-1.5'>
                                    <span className='font-bold text-foreground group-hover:text-primary transition-colors truncate block'>
                                      {project.list_name}
                                    </span>
                                    <div className='flex flex-wrap gap-2'>
                                      {project.budget_type && (
                                        <span className='px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider bg-orange-500/10 text-orange-600 border border-orange-500/20'>
                                          {project.budget_type}
                                        </span>
                                      )}
                                      {project.schedule_status && (
                                        <span className='px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider bg-blue-500/10 text-blue-600 border border-blue-500/20'>
                                          {project.schedule_status}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </TableCell>
                              )
                            case 'type':
                              return (
                                <TableCell
                                  key={column.key}
                                  className='truncate'
                                >
                                  <div className='flex items-center gap-1.5 text-xs text-muted-foreground'>
                                    <CheckCircle2 className='w-3 h-3 text-green-500' />
                                    {project.project_type}
                                  </div>
                                </TableCell>
                              )
                            case 'task_progress':
                              return (
                                <TableCell
                                  key={column.key}
                                  className='min-w-[200px]'
                                >
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className='cursor-help'>
                                        <ModernProgressBar
                                          progress={progress}
                                          label='Tasks'
                                          subLabel={`${completed}/${total} (${Math.round(progress)}%)`}
                                          colorClass='bg-gradient-to-r from-primary/80 to-primary'
                                        />
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent className='bg-black/95 backdrop-blur-xl border-white/10 p-3'>
                                      <div className='space-y-2'>
                                        <div className='text-xs font-bold text-white border-b border-white/10 pb-1'>
                                          Task Breakdown
                                        </div>
                                        <div className='grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]'>
                                          <span className='text-muted-foreground'>
                                            Completed:
                                          </span>
                                          <span className='text-emerald-400 font-bold text-right'>
                                            {completed}
                                          </span>
                                          <span className='text-muted-foreground'>
                                            Remaining:
                                          </span>
                                          <span className='text-orange-400 font-bold text-right'>
                                            {total - completed}
                                          </span>
                                          <span className='text-muted-foreground'>
                                            Total Tasks:
                                          </span>
                                          <span className='text-white font-bold text-right'>
                                            {total}
                                          </span>
                                          <span className='text-muted-foreground'>
                                            Success Rate:
                                          </span>
                                          <span className='text-primary font-bold text-right'>
                                            {Math.round(progress)}%
                                          </span>
                                        </div>
                                      </div>
                                    </TooltipContent>
                                  </Tooltip>
                                </TableCell>
                              )
                            case 'timeline':
                              return (
                                <TableCell
                                  key={column.key}
                                  className='truncate min-w-[260px]'
                                >
                                  {(() => {
                                    if (
                                      !project.start_date ||
                                      !project.due_date
                                    ) {
                                      const missing = !project.start_date
                                        ? 'Start'
                                        : 'Due'
                                      return (
                                        <ModernProgressBar
                                          progress={0}
                                          label={`${missing} Date`}
                                          subLabel='Missing'
                                          colorClass='bg-muted'
                                        />
                                      )
                                    }

                                    const start = dayjs(project.start_date)
                                    const due = dayjs(project.due_date)
                                    const today = dayjs()

                                    const totalDays = due.diff(start, 'day')
                                    const daysLeft = due.diff(today, 'day')
                                    const daysSpent = today.diff(start, 'day')
                                    const isOverdue = daysLeft < 0

                                    const percentageSpent =
                                      totalDays > 0
                                        ? Math.max(
                                            0,
                                            Math.min(
                                              100,
                                              (daysSpent / totalDays) * 100
                                            )
                                          )
                                        : 0

                                    const getBarColor = (
                                      dLeft: number,
                                      overdue: boolean
                                    ) => {
                                      if (overdue || dLeft <= 3)
                                        return 'bg-gradient-to-r from-destructive to-red-500'
                                      if (dLeft <= 7)
                                        return 'bg-gradient-to-r from-orange-400 to-orange-600'
                                      return 'bg-gradient-to-r from-emerald-400 to-emerald-600'
                                    }

                                    return (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <div className='cursor-help'>
                                            <ModernProgressBar
                                              progress={
                                                isOverdue
                                                  ? 100
                                                  : percentageSpent
                                              }
                                              label={
                                                isOverdue
                                                  ? 'Overdue'
                                                  : 'Timeline'
                                              }
                                              subLabel={
                                                isOverdue
                                                  ? `${Math.abs(daysLeft)}d Over`
                                                  : `${Math.max(0, daysLeft)}d left (${Math.round(percentageSpent)}%)`
                                              }
                                              colorClass={getBarColor(
                                                daysLeft,
                                                isOverdue
                                              )}
                                            />
                                          </div>
                                        </TooltipTrigger>
                                        <TooltipContent className='bg-black/95 backdrop-blur-xl border-white/10 p-3'>
                                          <div className='space-y-2'>
                                            <div className='text-xs font-bold text-white border-b border-white/10 pb-1'>
                                              Project Timeline
                                            </div>
                                            <div className='grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]'>
                                              <span className='text-muted-foreground'>
                                                Start Date:
                                              </span>
                                              <span className='text-white font-bold text-right'>
                                                {start.format('MMM DD, YYYY')}
                                              </span>
                                              <span className='text-muted-foreground'>
                                                Due Date:
                                              </span>
                                              <span className='text-white font-bold text-right'>
                                                {due.format('MMM DD, YYYY')}
                                              </span>
                                              <span className='text-muted-foreground'>
                                                Total Duration:
                                              </span>
                                              <span className='text-white font-bold text-right'>
                                                {totalDays} days
                                              </span>
                                              <span className='text-muted-foreground'>
                                                Days Remaining:
                                              </span>
                                              <span
                                                className={`${daysLeft < 0 ? 'text-destructive' : 'text-emerald-400'} font-bold text-right`}
                                              >
                                                {isOverdue
                                                  ? `${Math.abs(daysLeft)} days overdue`
                                                  : `${daysLeft} days`}
                                              </span>
                                            </div>
                                          </div>
                                        </TooltipContent>
                                      </Tooltip>
                                    )
                                  })()}
                                </TableCell>
                              )
                            case 'capacity':
                              return (
                                <TableCell
                                  key={column.key}
                                  className='truncate min-w-[260px]'
                                >
                                  {(() => {
                                    const capacity =
                                      Number(project.total_hours) || 0
                                    const billable =
                                      Number(
                                        project.billable_duration_seconds || 0
                                      ) / 3600
                                    const nonBillable =
                                      Number(
                                        project.nonbillable_duration_seconds ||
                                          0
                                      ) / 3600
                                    const totalTracked = billable + nonBillable
                                    const hoursLeft = capacity - totalTracked
                                    const isOverCapacity = hoursLeft < 0

                                    const capacityUsedPct =
                                      capacity > 0
                                        ? Math.max(
                                            0,
                                            Math.min(
                                              100,
                                              (totalTracked / capacity) * 100
                                            )
                                          )
                                        : 0

                                    const billablePct =
                                      totalTracked > 0
                                        ? (billable / totalTracked) * 100
                                        : 0

                                    const getCapacityColor = (pct: number) => {
                                      if (pct >= 100)
                                        return 'bg-gradient-to-r from-destructive to-red-500'
                                      if (pct >= 80)
                                        return 'bg-gradient-to-r from-orange-400 to-orange-600'
                                      return 'bg-gradient-to-r from-primary/80 to-primary'
                                    }

                                    return (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <div className='flex flex-col gap-3 cursor-help'>
                                            <ModernProgressBar
                                              progress={capacityUsedPct}
                                              label='Capacity'
                                              subLabel={`${formatHours(totalTracked)}/${capacity}h`}
                                              colorClass={getCapacityColor(
                                                capacityUsedPct
                                              )}
                                            />
                                            <ModernProgressBar
                                              progress={billablePct}
                                              label='Billable'
                                              subLabel={`${Math.round(billablePct)}%`}
                                              colorClass='bg-gradient-to-r from-emerald-400 to-emerald-600'
                                            />
                                            {nonBillable > billable &&
                                              totalTracked > 0 && (
                                                <div className='px-0.5 flex items-center gap-1.5'>
                                                  <AlertCircle className='w-3 h-3 text-destructive animate-pulse' />
                                                  <span className='text-[9px] text-destructive font-black uppercase tracking-tighter animate-pulse'>
                                                    High Non-Billable Ratio
                                                  </span>
                                                </div>
                                              )}
                                          </div>
                                        </TooltipTrigger>
                                        <TooltipContent className='bg-black/95 backdrop-blur-xl border-white/10 p-3'>
                                          <div className='space-y-2'>
                                            <div className='text-xs font-bold text-white border-b border-white/10 pb-1'>
                                              Resource Allocation
                                            </div>
                                            <div className='grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]'>
                                              <span className='text-muted-foreground'>
                                                Total Budget:
                                              </span>
                                              <span className='text-white font-bold text-right'>
                                                {formatHours(capacity)}h
                                              </span>
                                              <span className='text-muted-foreground'>
                                                Total Tracked:
                                              </span>
                                              <span className='text-white font-bold text-right'>
                                                {formatHours(totalTracked)}h
                                              </span>
                                              <span className='text-muted-foreground'>
                                                Billable:
                                              </span>
                                              <span className='text-emerald-400 font-bold text-right'>
                                                {formatHours(billable)}h
                                              </span>
                                              <span className='text-muted-foreground'>
                                                Non-Billable:
                                              </span>
                                              <span className='text-orange-400 font-bold text-right'>
                                                {formatHours(nonBillable)}h
                                              </span>
                                              <span className='text-muted-foreground'>
                                                Remaining:
                                              </span>
                                              <span
                                                className={`${isOverCapacity ? 'text-destructive' : 'text-emerald-400'} font-bold text-right`}
                                              >
                                                {formatHours(hoursLeft)}h
                                              </span>
                                            </div>
                                          </div>
                                        </TooltipContent>
                                      </Tooltip>
                                    )
                                  })()}
                                </TableCell>
                              )
                            case 'profit_budget':
                              return (
                                <TableCell
                                  key={column.key}
                                  className='min-w-[260px]'
                                >
                                  {(() => {
                                    const revenue =
                                      Number(project.billable_revenue) || 0
                                    const expenses =
                                      Number(project.total_employee_cost) ||
                                      revenue - (Number(project.profit) || 0)
                                    const profit = revenue - expenses
                                    const margin =
                                      revenue > 0 ? (profit / revenue) * 100 : 0
                                    const isProfit = profit >= 0

                                    return (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <div className='flex flex-col gap-3 cursor-help'>
                                            <ModernProgressBar
                                              progress={margin}
                                              label='Margin'
                                              subLabel={`${Math.round(margin)}%`}
                                              colorClass={
                                                isProfit
                                                  ? 'bg-gradient-to-r from-emerald-400 to-emerald-600'
                                                  : 'bg-gradient-to-r from-destructive to-red-500'
                                              }
                                            />
                                            <div className='flex justify-between items-center px-0.5'>
                                              <div className='flex items-center gap-1.5'>
                                                {isProfit ? (
                                                  <TrendingUp className='w-3.5 h-3.5 text-emerald-500' />
                                                ) : (
                                                  <TrendingDown className='w-3.5 h-3.5 text-destructive' />
                                                )}
                                                <span
                                                  className={`text-[11px] font-black ${isProfit ? 'text-emerald-600' : 'text-destructive'}`}
                                                >
                                                  {formatCurrency(profit)}
                                                </span>
                                              </div>
                                              <span
                                                className={`text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-md ${
                                                  isProfit
                                                    ? 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/20'
                                                    : 'bg-destructive/10 text-destructive border border-destructive/20'
                                                }`}
                                              >
                                                {isProfit
                                                  ? 'Healthy'
                                                  : 'At Risk'}
                                              </span>
                                            </div>
                                          </div>
                                        </TooltipTrigger>
                                        <TooltipContent className='bg-black/95 backdrop-blur-xl border-white/10 p-3'>
                                          <div className='space-y-2'>
                                            <div className='text-xs font-bold text-white border-b border-white/10 pb-1'>
                                              Financial Performance
                                            </div>
                                            <div className='grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]'>
                                              <span className='text-muted-foreground'>
                                                Billable Revenue:
                                              </span>
                                              <span className='text-emerald-400 font-bold text-right'>
                                                {formatCurrency(revenue)}
                                              </span>
                                              {isFullFinance && (
                                                <>
                                                  <span className='text-muted-foreground'>
                                                    Employee Cost:
                                                  </span>
                                                  <span className='text-orange-400 font-bold text-right'>
                                                    {formatCurrency(expenses)}
                                                  </span>
                                                </>
                                              )}
                                              <span className='text-muted-foreground'>
                                                Gross Profit:
                                              </span>
                                              <span
                                                className={`${isProfit ? 'text-emerald-400' : 'text-destructive'} font-bold text-right`}
                                              >
                                                {formatCurrency(profit)}
                                              </span>
                                              <span className='text-muted-foreground'>
                                                Profit Margin:
                                              </span>
                                              <span
                                                className={`${isProfit ? 'text-emerald-400' : 'text-destructive'} font-bold text-right`}
                                              >
                                                {Math.round(margin)}%
                                              </span>
                                            </div>
                                          </div>
                                        </TooltipContent>
                                      </Tooltip>
                                    )
                                  })()}
                                </TableCell>
                              )
                            case 'symptoms':
                              return (
                                <TableCell
                                  key={column.key}
                                  className='min-w-[300px]'
                                >
                                  {(() => {
                                    const symptoms: {
                                      label: string
                                      level:
                                        | 'critical'
                                        | 'warning'
                                        | 'info'
                                        | 'healthy'
                                      description: string
                                    }[] = []

                                    const revenue =
                                      Number(project.billable_revenue) || 0
                                    const expenses =
                                      Number(project.total_employee_cost) ||
                                      revenue - (Number(project.profit) || 0)
                                    const profit = revenue - expenses
                                    const margin =
                                      revenue > 0 ? (profit / revenue) * 100 : 0

                                    const billable =
                                      Number(
                                        project.billable_duration_seconds || 0
                                      ) / 3600
                                    const nonBillable =
                                      Number(
                                        project.nonbillable_duration_seconds ||
                                          0
                                      ) / 3600
                                    const totalTracked = billable + nonBillable

                                    const capacity =
                                      Number(project.total_hours) || 0
                                    const hoursLeft = capacity - totalTracked

                                    const start = project.start_date
                                      ? dayjs(project.start_date)
                                      : null
                                    const due = project.due_date
                                      ? dayjs(project.due_date)
                                      : null
                                    const today = dayjs()
                                    const daysLeft = due
                                      ? due.diff(today, 'day')
                                      : 0

                                    // Critical
                                    if (canSeeFinance && profit < 0)
                                      symptoms.push({
                                        label: 'Net Loss',
                                        level: 'critical',
                                        description:
                                          'Expenses exceed revenue, resulting in a financial loss.',
                                      })
                                    if (
                                      due &&
                                      daysLeft < 0 &&
                                      Number(project.completed_tasks) <
                                        Number(project.total_tasks)
                                    )
                                      symptoms.push({
                                        label: 'Overdue',
                                        level: 'critical',
                                        description:
                                          'The project due date has passed with remaining tasks.',
                                      })
                                    if (start && due && start.isAfter(due))
                                      symptoms.push({
                                        label: 'Date Mismatch',
                                        level: 'critical',
                                        description:
                                          'The start date is set after the due date.',
                                      })
                                    if (hoursLeft < 0)
                                      symptoms.push({
                                        label: 'Over Capacity',
                                        level: 'critical',
                                        description:
                                          'Tracked hours have exceeded the total allocated budget.',
                                      })

                                    // Warning
                                    if (
                                      nonBillable > billable &&
                                      totalTracked > 0
                                    )
                                      symptoms.push({
                                        label: 'High NB Ratio',
                                        level: 'warning',
                                        description:
                                          'Non-billable hours are higher than billable hours.',
                                      })
                                    if (
                                      canSeeFinance &&
                                      margin >= 0 &&
                                      margin < 20
                                    )
                                      symptoms.push({
                                        label: 'Low Margin',
                                        level: 'warning',
                                        description:
                                          'Profit margin is below the healthy threshold of 20%.',
                                      })
                                    if (
                                      !project.start_date ||
                                      !project.due_date
                                    )
                                      symptoms.push({
                                        label: 'Missing Dates',
                                        level: 'warning',
                                        description:
                                          'Project is missing either a start date or a due date.',
                                      })
                                    if (Number(project.total_tasks) === 0)
                                      symptoms.push({
                                        label: 'No Tasks',
                                        level: 'warning',
                                        description:
                                          'No tasks have been found for this project.',
                                      })

                                    if (
                                      symptoms.length === 0 ||
                                      (symptoms.length === 1 &&
                                        symptoms[0]?.level === 'info')
                                    )
                                      symptoms.push({
                                        label: 'Healthy',
                                        level: 'healthy',
                                        description:
                                          'Project is performing within all healthy parameters.',
                                      })

                                    return (
                                      <div className='flex flex-wrap gap-1'>
                                        {symptoms.map((s, i) => (
                                          <Tooltip key={i} delayDuration={300}>
                                            <TooltipTrigger asChild>
                                              <div
                                                className={cn(
                                                  'px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest border cursor-help flex items-center gap-1.5 transition-all duration-300 hover:scale-105',
                                                  s.level === 'critical'
                                                    ? 'bg-destructive/10 text-destructive border-destructive/20 hover:bg-destructive/20'
                                                    : s.level === 'warning'
                                                      ? 'bg-orange-500/10 text-orange-600 border-orange-500/20 hover:bg-orange-500/20'
                                                      : s.level === 'info'
                                                        ? 'bg-blue-500/10 text-blue-600 border-blue-500/20 hover:bg-blue-500/20'
                                                        : 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20 hover:bg-emerald-500/20'
                                                )}
                                              >
                                                <div
                                                  className={cn(
                                                    'w-1.5 h-1.5 rounded-full',
                                                    s.level === 'critical'
                                                      ? 'bg-destructive animate-pulse'
                                                      : s.level === 'warning'
                                                        ? 'bg-orange-500'
                                                        : s.level === 'info'
                                                          ? 'bg-blue-500'
                                                          : 'bg-emerald-500'
                                                  )}
                                                />
                                                {s.label}
                                              </div>
                                            </TooltipTrigger>
                                            <TooltipContent className='max-w-[220px] text-center bg-black/95 backdrop-blur-xl border-white/10 p-3 shadow-2xl'>
                                              <div className='space-y-1.5'>
                                                <div
                                                  className={cn(
                                                    'text-[9px] font-black uppercase tracking-tighter',
                                                    s.level === 'critical'
                                                      ? 'text-destructive'
                                                      : s.level === 'warning'
                                                        ? 'text-orange-500'
                                                        : s.level === 'info'
                                                          ? 'text-blue-500'
                                                          : 'text-emerald-500'
                                                  )}
                                                >
                                                  {s.level}
                                                </div>
                                                <p className='text-[11px] leading-relaxed font-semibold text-white/90'>
                                                  {s.description}
                                                </p>
                                              </div>
                                            </TooltipContent>
                                          </Tooltip>
                                        ))}
                                      </div>
                                    )
                                  })()}
                                </TableCell>
                              )
                            default:
                              return null
                          }
                        })}
                        <TableCell className='sticky right-0 z-10 bg-background/95 backdrop-blur-sm shadow-[-8px_0_12px_-12px_rgba(0,0,0,0.35)] opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-right pr-4'>
                          <Button
                            variant='outline'
                            size='sm'
                            onClick={() => handleOpenModal(project)}
                            className='w-11 bg-background border-border hover:bg-accent hover:text-accent-foreground'
                          >
                            <Eye className='w-4 h-4' />
                          </Button>
                        </TableCell>
                      </motion.tr>
                    )
                  })
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={visibleColumnsCount}
                      className='h-64 text-center text-muted-foreground'
                    >
                      <div className='flex flex-col items-center gap-3'>
                        <div className='p-4 bg-muted/50 rounded-full'>
                          <Search className='w-8 h-8 opacity-20' />
                        </div>
                        <p className='text-sm font-medium'>
                          No project health data found matching your filters.
                        </p>
                        <Button
                          variant='link'
                          onClick={handleReset}
                          className='text-primary'
                        >
                          Clear all filters
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </AnimatePresence>
            </TableBody>
          </Table>
        </div>

        {totalPages > 1 && (
          <div className='shrink-0 pt-6'>
            <CustomPagination
              limit={limit}
              setLimit={setLimit}
              page={page}
              setPage={setPage}
              totalPages={totalPages}
              totalRecords={totalRecords}
            />
          </div>
        )}

        <HealthModel
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          project={selectedProject}
          financeAccess={
            isFullFinance ? 'full' : canSeeFinance ? 'mini' : 'none'
          }
        />
      </div>
    </TooltipProvider>
  )
}

export default Health
